import React from "react";
import styled from "@emotion/styled";
import {
  fontFamily,
  fontSize,
  spacing,
  border as borderToken,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";

const Container = styled.div`
  margin-bottom: ${spacing[12]};
`;

const Title = styled.h3`
  font-size: ${fontSize.lg};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const SVGContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing[8]};
`;

// Node positions for the dependency graph
const nodes = [
  { id: "tokens", label: "@atlas-int/tokens", x: 150, y: 200, level: 0 },
  { id: "data", label: "@atlas-int/data", x: 400, y: 200, level: 0 },
  { id: "ui", label: "@atlas-int/ui", x: 150, y: 110, level: 1 },
  { id: "controls", label: "@atlas-int/controls", x: 150, y: 20, level: 2 },
  { id: "viz", label: "@atlas-int/viz", x: 400, y: 20, level: 2 },
];

const edges = [
  { from: "ui", to: "tokens" },
  { from: "controls", to: "tokens" },
  { from: "controls", to: "ui" },
  { from: "controls", to: "data" },
  { from: "viz", to: "tokens" },
  { from: "viz", to: "ui" },
  { from: "viz", to: "data" },
];

const nodeWidth = 160;
const nodeHeight = 32;

const getNode = (id: string) => nodes.find((n) => n.id === id)!;

const levelColors = [interactive.default, text.secondary, text.primary];

const DependencyGraph: React.FC = () => {
  return (
    <Container>
      <Title>Package Dependency Graph</Title>
      <SVGContainer>
        <svg width="580" height="240" viewBox="-10 -5 590 250">
          <defs>
            <marker
              id="arrow"
              viewBox="0 0 10 6"
              refX="10"
              refY="3"
              markerWidth="8"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 0 L 10 3 L 0 6 Z" fill={borderToken.default} />
            </marker>
          </defs>

          {/* Edges */}
          {edges.map(({ from, to }) => {
            const fromNode = getNode(from);
            const toNode = getNode(to);
            const x1 = fromNode.x + nodeWidth / 2;
            const y1 = fromNode.y + nodeHeight;
            const x2 = toNode.x + nodeWidth / 2;
            const y2 = toNode.y;
            return (
              <line
                key={`${from}-${to}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={borderToken.default}
                strokeWidth={1.5}
                markerEnd="url(#arrow)"
              />
            );
          })}

          {/* Nodes */}
          {nodes.map((node) => (
            <g key={node.id}>
              <rect
                x={node.x}
                y={node.y}
                width={nodeWidth}
                height={nodeHeight}
                rx={6}
                ry={6}
                fill={surface.raised}
                stroke={levelColors[node.level] ?? borderToken.default}
                strokeWidth={2}
              />
              <text
                x={node.x + nodeWidth / 2}
                y={node.y + nodeHeight / 2 + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={text.primary}
                fontFamily={fontFamily.mono}
                fontSize="11"
              >
                {node.label}
              </text>
            </g>
          ))}

          {/* Level labels */}
          <text x={560} y={215} fill={text.muted} fontSize="10" textAnchor="end">
            L0: Foundation
          </text>
          <text x={560} y={125} fill={text.muted} fontSize="10" textAnchor="end">
            L1: Primitives
          </text>
          <text x={560} y={35} fill={text.muted} fontSize="10" textAnchor="end">
            L2: Features
          </text>
        </svg>
      </SVGContainer>
    </Container>
  );
};

export default DependencyGraph;
